// Define selectors for common focusable elements.
const excludeSelector = ':not([disabled]):not([tabindex="-1"])';
const selectors = ['[tabindex]', 'a[href]', 'button', 'details', 'input:not([type="hidden"])', 'select', 'textarea'];
const selector = selectors.join(excludeSelector + ',') + excludeSelector;
export const queryFocusable = element => {
  return Array.from(element.querySelectorAll(selector)).filter(element => {
    if (element.getClientRects().length <= 0 || element.hasAttribute('disabled')) {
      return false;
    }
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
};
export const focusAfter = (relativeElement = document.activeElement, allowWrap = true) => {
  const allFocusable = queryFocusable(document.body);
  if (allFocusable.length === 0) {
    return;
  }

  // Compare the given node the other focus-able nodes.
  for (const otherElement of allFocusable) {
    const relativePosition = relativeElement.compareDocumentPosition(otherElement);
    if (relativePosition & Node.DOCUMENT_POSITION_FOLLOWING && !(relativePosition & Node.DOCUMENT_POSITION_CONTAINS) && !(relativePosition & Node.DOCUMENT_POSITION_CONTAINED_BY)) {
      otherElement.focus();
      return;
    }
  }

  // Wrap back to the fist focus-able element.
  if (allowWrap) {
    allFocusable[0].focus();
  }
};
export const skipAncestor = (attributeName = 'data-skip-ancestor') => {
  const processButton = button => {
    button.addEventListener('click', () => {
      let ancestor = button.parentNode;
      const parentSelector = button.getAttribute(attributeName);
      if (parentSelector) {
        ancestor = button.closest(parentSelector);
      }
      if (ancestor) {
        focusAfter(ancestor);
      }
    });
  };
  const buttons = document.querySelectorAll('[' + attributeName + ']');
  for (const button of buttons) {
    processButton(button);
  }
};
export const focusOnFirst = element => {
  // Convert a selector to an element.
  if (typeof element === 'string') {
    element = document.body.querySelector(element);
  }
  // Exit now if no element is provided.
  if (!element) {
    return;
  }

  // Query all potentially focusable elements within the document.
  const focusableElements = queryFocusable(element);

  // Iterate through the NodeList to find the first element that can be focused.
  for (let i = 0; i < focusableElements.length; i++) {
    const focusableElement = focusableElements[i];
    // Check if the element is visible.
    if (focusableElement.offsetWidth > 0 || focusableElement.offsetHeight > 0 || focusableElement.getClientRects().length > 0) {
      // Focus on the first visible element.
      focusableElement.focus();
      break;
    }
  }
};

// In order to know which focus trap is active a continuously incremented counter is used. This is stored as an index per focus trap call and only when this index is the last in the list should the function be active. This allows the focus trap to be released while a more recent one is still active.
let trappedCount = 0;
const trapList = [];
export const trapFocus = element => {
  if (typeof element === 'string') {
    element = document.body.querySelector(element);
  }
  if (!element) {
    return false;
  }
  const focusedBefore = document.activeElement;
  let trapIndex = trappedCount;
  trappedCount++;
  trapList.push(trapIndex);
  const listener = event => {
    if (event.key !== 'Tab' || trapList[trapList.length - 1] !== trapIndex) {
      return;
    }
    const focusableElements = queryFocusable(element).filter(element => element.getAttribute('data-purpose') !== 'skip-navigation');
    if (focusableElements.length) {
      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (event.shiftKey) {
        // ⇧+Tab on first -> go to last.
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        // Tab on last -> go to first.
        event.preventDefault();
        first.focus();
      }
    }
  };
  element.addEventListener('keydown', listener);

  // element.dispatchEvent(
  // 	new CustomEvent('focus-trapped', {
  // 		detail: element,
  // 	})
  // )

  return () => {
    // Remove from focus list.
    const index = trapList.indexOf(trapIndex);
    if (index >= 0) {
      trapList.splice(index, 1);
    }

    // Stop listening.
    element.removeEventListener('keydown', listener);

    // Return focus to last known focussed element.
    if (focusedBefore) {
      focusedBefore.focus();
    }

    // element.dispatchEvent(
    // 	new CustomEvent('focus-released', {
    // 		detail: element,
    // 	})
    // )
  };
};